import React, { useState, useEffect } from "react";
import { useWorkspace } from "../WorkspaceContext";
import { useAuth } from "../AuthContext";
import { http, formatApiError } from "../api";
import { useConfirm } from "../components/ConfirmDialog";
import { FEATURES } from "../UpgradeContext";
import UpgradeButton from "../components/UpgradeButton";

const Check = ({ s = 13 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M20 6 9 17l-5-5" /></svg>;
const Star = ({ s = 13 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M12 1.5l1.9 6.1 6.1 1.9-6.1 1.9L12 17.5l-1.9-6.1L4 9.5l6.1-1.9z" /></svg>;

export default function Scholarships({ go }) {
  const { scholarships, addScholarship, updateScholarship, deleteScholarship, universities, profile, refresh } = useWorkspace();
  const { user } = useAuth();
  const isNavigator = !!user?.plan_summary?.is_navigator;
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("");
  const [filter, setFilter] = useState("all"); // all | saved | verified

  const [q, setQ] = useState({
    citizenship: profile?.citizenship || profile?.student_country || "",
    student_country: profile?.student_country || profile?.citizenship || "",
    student_level: profile?.intended_level || "",
    program: profile?.intended_major || "",
    intake_term: profile?.intake_term || "",
    budget_need: profile?.funding_need || "partial",
    fields_of_study: (profile?.strongest_subjects || []).join(", ") || "",
    keywords: "",
    notes: "",
  });

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("vidhya_schol_prefill");
      if (raw) {
        const { keywords, notes } = JSON.parse(raw);
        setQ((p) => ({ ...p, keywords: keywords || p.keywords, notes: notes || p.notes }));
        if (keywords) setText(keywords);
        sessionStorage.removeItem("vidhya_schol_prefill");
      }
    } catch (e) { /* ignore */ }
  }, []);

  const scan = async () => {
    setBusy(true); setErr(""); setPhase("researching");
    try {
      const targets = universities.map((u) => u.name);
      const payload = {
        ...q, keywords: text || q.keywords,
        fields_of_study: q.fields_of_study ? q.fields_of_study.split(",").map((s) => s.trim()).filter(Boolean) : [],
        target_universities: targets,
      };
      // Kick off the deep search as a BACKGROUND job (avoids the 60s request cap),
      // then poll its status until ready and reload the persisted results.
      const { data: started } = await http.post("/ai/find-scholarships/start", payload);
      const jobId = started.job_id;
      const deadline = Date.now() + 120000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await http.get(`/ai/find-scholarships/status/${jobId}`);
        if (st.status === "researching" || st.status === "verifying") { setPhase(st.status); continue; }
        if (st.status === "ready") { await refresh(); setText(""); break; }
        if (st.status === "error") { setErr(st.error || "Scholarship search failed"); break; }
      }
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); setPhase(""); }
  };

  const toggleSave = async (s) => {
    const next = !s.saved;
    await updateScholarship(s.id, { saved: next });
    if (next && (s.docs_required?.length || s.deadline)) {
      const ok = await confirm({ title: "Add a reminder task?", message: `Add "${s.name}" to your Task Center with a deadline reminder?`, confirmLabel: "Add task" });
      if (ok) { try { await http.post(`/scholarships/${s.id}/seed-tasks`); await refresh(); } catch (e) { /* non-blocking */ } }
    }
  };

  const visible = scholarships.filter((s) => {
    if (text && !`${s.name || ""} ${s.provider || s.uni || ""}`.toLowerCase().includes(text.toLowerCase())) return false;
    if (filter === "saved") return s.saved;
    if (filter === "verified") return s.verified;
    return !s.saved; // "All" hides saved — they live behind the Saved tab
  });
  const savedCount = scholarships.filter((s) => s.saved).length;

  const COV_LABELS = { tuition: "Tuition", stipend: "Stipend", living: "Living", travel: "Travel", books: "Books", insurance: "Insurance", partial: "Partial", full: "Full ride", fees: "Fees" };
  const coverageChips = (s) => {
    const raw = (s.coverage || (typeof s.amount === "string" && !/\d|\$|£|€|varies/i.test(s.amount) ? s.amount : "")) || "";
    return raw.split(/[+,/]|\band\b|·|\|/i).map((c) => c.trim().toLowerCase()).filter(Boolean)
      .map((c) => COV_LABELS[c] || (c.charAt(0).toUpperCase() + c.slice(1)))
      .filter((v, i, a) => v && a.indexOf(v) === i).slice(0, 4);
  };
  const amountFig = (s) => {
    const a = s.amount || "";
    if (a && /\d|\$|£|€|full|ride|varies/i.test(a)) return a;
    return "Varies";
  };

  const Card = ({ s }) => {
    const cov = coverageChips(s);
    const conf = s.confidence || (s.verified ? "verified" : "found");
    const amountOk = s.amount_verified === true;
    const [removing, setRemoving] = useState(false);
    const removeSchol = async () => {
      const ok = await confirm({ title: "Remove scholarship?", message: `Remove "${s.name}" from your list?`, confirmLabel: "Remove", danger: true });
      if (!ok) return;
      setRemoving(true);
      try { await deleteScholarship(s.id); } catch (e) { console.warn("delete scholarship failed", e); setRemoving(false); }
    };
    const applyUrl = s.url || s.explore_url ||
      `https://www.google.com/search?q=${encodeURIComponent(`${s.name} ${s.provider || s.uni || ""} scholarship`)}`;
    return (
    <div className="schcard" data-testid={`schol-card-${s.id}`}>
      <div className="amt">
        <span className="amt-fig" style={amountOk ? { display: "inline-flex", alignItems: "center", gap: 4, color: "var(--green, #788c5d)" } : undefined}>
          {amountOk && <Check s={12} />}{amountFig(s)}
        </span>
        <span className="amt-cap">{amountOk ? "Award · verified" : "Est. award"}</span>
      </div>
      <div className="si">
        <b>{s.name}</b>
        <div className="sm">
          {s.match && <span className="matchbar"><Star s={12} />{s.match}{typeof s.match === "number" ? "% match" : ""}</span>}
          {s.deadline && <span>Deadline {s.deadline}</span>}
          {s.provider || s.uni ? <span>{s.provider || s.uni}</span> : null}
          {conf === "verified"
            ? <span className="vbadge" data-testid={`schol-badge-${s.id}`}><Check s={12} />verified</span>
            : <span className="vbadge unv" data-testid={`schol-badge-${s.id}`}>confirm on site</span>}
        </div>
        {cov.length > 0 && (
          <div className="covrow" data-testid={`schol-coverage-${s.id}`}>
            {cov.map((c) => <span key={c} className="covpill"><span className="cd" />{c}</span>)}
          </div>
        )}
      </div>
      <div className="sa">
        <button className={`saveb ${s.saved ? "saved" : ""}`} onClick={() => toggleSave(s)} data-testid={`schol-save-${s.id}`}>
          {s.saved ? <><Check s={13} />Saved</> : "Save"}
        </button>
        <a className="schol-open" href={applyUrl} target="_blank" rel="noreferrer" data-testid={`schol-apply-${s.id}`}>Open ↗</a>
        <button className="schol-del" onClick={removeSchol} disabled={removing} data-testid={`schol-delete-${s.id}`}
                title="Remove scholarship" aria-label={`Remove ${s.name}`}>
          {removing ? "…" : "Delete"}
        </button>
      </div>
    </div>
    );
  };

  return (
    <div className="vwrap vschol" data-testid="scholarships-view">
      <div className="ptitle">Scholarships</div>
      <div className="psub" style={{ marginBottom: 18 }}>Search, match to your profile, and save the ones worth chasing.</div>

      {scholarships.length > 0 && (
        <div className="schbanner" data-testid="schol-banner">
          <div className="sbi"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="6" /><path d="M8 14l-1 8 5-3 5 3-1-8" /></svg></div>
          <div>
            <b>Don't let cost stop you.</b><br />
            <span>Vidhya found {scholarships.length} scholarship{scholarships.length === 1 ? "" : "s"} tied to your list — save the ones worth chasing and we'll remind you.</span>
          </div>
        </div>
      )}

      {!isNavigator && (
        <div className="explorer-board-banner" data-testid="schol-explorer-banner">
          <div><strong>Explorer · basic scholarship search</strong><p>Navigator unlocks personalised matching across govt, foundation and university funds, tuned to your profile.</p></div>
          <UpgradeButton feature={FEATURES.SCHOLARSHIPS} context="schol_banner" label="Unlock matching" size="sm" testid="schol-banner-upgrade-btn" />
        </div>
      )}

      <div className="schtop">
        <div className="searchbar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.4-3.4" /></svg>
          <input placeholder="Search scholarships — by country, field, or amount…" value={text}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") scan(); }} data-testid="schol-search" />
        </div>
        <button className="vp-cta" onClick={scan} disabled={busy} data-testid="schol-scan-btn" style={{ borderRadius: 13 }}>
          <Star s={15} />{busy ? (phase === "verifying" ? "Verifying sources…" : phase === "researching" ? "Researching funds…" : "Searching…") : "Find with Vidhya"}
        </button>
      </div>

      <div className="filt" style={{ marginBottom: 18 }}>
        <button className={`fbtn ${filter === "all" ? "on" : ""}`} onClick={() => setFilter("all")} data-testid="schol-filter-all">All</button>
        <button className={`fbtn ${filter === "saved" ? "on" : ""}`} onClick={() => setFilter("saved")} data-testid="schol-filter-saved">Saved{savedCount > 0 ? ` (${savedCount})` : ""}</button>
        <button className={`fbtn ${filter === "verified" ? "on" : ""}`} onClick={() => setFilter("verified")} data-testid="schol-filter-verified">Verified</button>
      </div>

      {err && <div className="berr" data-testid="schol-error" style={{ color: "var(--warn)", fontWeight: 600, marginBottom: 12 }}>{err}</div>}

      {scholarships.length === 0 && !busy && (
        <div className="vempty" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-card)", padding: 34 }} data-testid="schol-empty">
          No scholarships yet. Search above and Vidhya will match funding to your profile and university list.
        </div>
      )}

      {visible.length > 0 && (
        <>
          <div className="sec-h">
            <h3>{filter === "saved" ? "Saved & stored" : filter === "verified" ? "Verified" : "Matched for you"}</h3>
            <span className="mt">
              {visible.length} result{visible.length === 1 ? "" : "s"}
              {filter === "saved" ? " · synced to your aid plan" : filter === "all" ? " · live" : ""}
            </span>
          </div>
          {visible.map((s) => <Card key={s.id} s={s} />)}
        </>
      )}

      {scholarships.length > 0 && visible.length === 0 && !busy && (
        <div className="vempty" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-card)", padding: 34 }} data-testid="schol-filter-empty">
          {filter === "saved"
            ? "No saved scholarships yet. Tap “Save” on any match to keep it here."
            : filter === "verified"
            ? "No verified scholarships yet. Verified awards confirmed against the official source will appear here."
            : "No scholarships match your search. Try a different keyword or run a new search."}
        </div>
      )}
    </div>
  );
}
